import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useFacilitatorPromotionNotice } from '../../hooks';

import './index.scss';
import messages from './messages';

const TITLE_ID = 'facilitator-promotion-notice-title';

/**
 * One-time congratulation shown the first time the portal observes that the
 * signed-in user holds the Facilitator role. Dismissable — it blocks nothing.
 */
const FacilitatorPromotionNotice = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { shouldShow, dismiss } = useFacilitatorPromotionNotice();
  const cardRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!shouldShow) {
      return undefined;
    }

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    cardRef.current?.focus();

    // Restore focus so a keyboard user dismissing the notice does not land on document.body.
    return () => previouslyFocusedRef.current?.focus?.();
  }, [shouldShow]);

  useEffect(() => {
    if (!shouldShow) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shouldShow, dismiss]);

  if (!shouldShow) {
    return null;
  }

  const handleLearnMore = () => {
    dismiss();
    navigate('/cohorts');
  };

  return (
    <div className="facilitator-promotion-notice">
      <div className="facilitator-promotion-notice-overlay" />
      <div
        className="facilitator-promotion-notice-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        ref={cardRef}
      >
        <div className="facilitator-promotion-notice__title" id={TITLE_ID}>
          <span className="facilitator-promotion-notice__emoji" aria-hidden="true">🎉</span>
          {intl.formatMessage(messages.title)}
        </div>
        <div className="facilitator-promotion-notice__text">
          {intl.formatMessage(messages.text)}
        </div>
        <div className="facilitator-promotion-notice__buttons">
          <button type="button" className="course-btn" onClick={handleLearnMore}>
            {intl.formatMessage(messages.cta)}
          </button>
          <button type="button" className="course-btn lm" onClick={dismiss}>
            {intl.formatMessage(messages.dismiss)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FacilitatorPromotionNotice;
